/**
 * CookieConsentBanner — GDPR-compliant granular cookie consent.
 *
 * Implements Art. 7 (consent) + Art. 13 (information) of GDPR.
 * Blocks GA4 analytics + marketing pixels until user explicitly consents.
 * Stores preferences in localStorage (`nutramilo_cookie_consent` key).
 *
 * Categories:
 *   - necessary: always on (auth, session, payment)
 *   - analytics: GA4, Yandex Metrica
 *   - marketing: Facebook Pixel, LinkedIn Insight (future)
 *
 * Exported helpers:
 *   - getCookieConsent(): { necessary, analytics, marketing } or null
 *   - hasAnalyticsConsent(): boolean
 *   - hasMarketingConsent(): boolean
 */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Shield, Cookie, ChevronDown, ChevronUp, X } from "lucide-react";

const CONSENT_KEY = "nutramilo_cookie_consent";
const AUTH_PATHS = ["/login", "/signup", "/register", "/forgot-password", "/reset-password", "/magic-link", "/activate"];
const CONSENT_VERSION = 1;

// ─── Public API ───
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}
export const hasAnalyticsConsent = () => getCookieConsent()?.analytics === true;
export const hasMarketingConsent = () => getCookieConsent()?.marketing === true;

// ─── Apply consent to GA4 (idempotent) ───
function applyConsent(consent) {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
        ad_user_data: consent.marketing ? "granted" : "denied",
        ad_personalization: consent.marketing ? "granted" : "denied",
      });
    }
  } catch {
    // ignore — gtag not loaded yet
  }
}

const TX = {
  bg: {
    title: "Бисквитки и поверителност",
    body: "Използваме бисквитки, за да работи сайтът коректно (необходими) и за да измерваме ефективност (по избор). Можете да изберете кои категории да приемете. Виж нашата",
    privacy_link: "Политика за поверителност",
    necessary_t: "Необходими",
    necessary_d: "Сесия, вход, плащания, защита. Не могат да бъдат изключени.",
    analytics_t: "Аналитични",
    analytics_d: "Помагат ни да разберем как се използва платформата (Google Analytics).",
    marketing_t: "Маркетинг",
    marketing_d: "Персонализирани реклами и проследяване на ефективност (бъдещи интеграции).",
    accept_all: "Приеми всички",
    reject_all: "Само необходими",
    save: "Запази избора ми",
    customize: "Настрой",
    hide: "Скрий настройките",
  },
  en: {
    title: "Cookies & Privacy",
    body: "We use cookies for the site to work properly (necessary) and to measure performance (optional). You can choose which categories to accept. See our",
    privacy_link: "Privacy Policy",
    necessary_t: "Necessary",
    necessary_d: "Session, login, payments, security. Cannot be disabled.",
    analytics_t: "Analytics",
    analytics_d: "Help us understand how the platform is used (Google Analytics).",
    marketing_t: "Marketing",
    marketing_d: "Personalized ads and performance tracking (future integrations).",
    accept_all: "Accept all",
    reject_all: "Only necessary",
    save: "Save my choice",
    customize: "Customize",
    hide: "Hide settings",
  },
  es: {
    title: "Cookies y privacidad",
    body: "Usamos cookies para que el sitio funcione (necesarias) y para medir el rendimiento (opcionales). Puedes elegir que categorias aceptar. Consulta nuestra",
    privacy_link: "Politica de privacidad",
    necessary_t: "Necesarias",
    necessary_d: "Sesion, inicio, pagos, seguridad. No se pueden desactivar.",
    analytics_t: "Analiticas",
    analytics_d: "Nos ayudan a entender como se usa la plataforma (Google Analytics).",
    marketing_t: "Marketing",
    marketing_d: "Anuncios personalizados y seguimiento de rendimiento (integraciones futuras).",
    accept_all: "Aceptar todas",
    reject_all: "Solo necesarias",
    save: "Guardar mi eleccion",
    customize: "Personalizar",
    hide: "Ocultar ajustes",
  },
};

export default function CookieConsentBanner() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { pathname } = useLocation();
  const t = TX[language] || TX.en;

  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      // Default deny until user makes a choice — GDPR Art. 7
      applyConsent({ analytics: false, marketing: false });
      setVisible(true);
    } else {
      applyConsent(existing);
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
  }, []);

  const persist = (consent) => {
    try {
      const record = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        decided_at: new Date().toISOString(),
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
      applyConsent(record);
    } catch {
      // localStorage unavailable
    }
    setVisible(false);
  };

  // Auth screens are short forms — the banner would cover the submit button
  // on mobile. It re-appears on the next page (consent is still pending).
  if (!visible || AUTH_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label="Cookie consent"
      className={`fixed bottom-2 inset-x-2 sm:bottom-3 sm:inset-x-auto sm:right-4 sm:max-w-md z-[10050] rounded-xl sm:rounded-2xl border shadow-2xl ${
        isDark ? "bg-[hsl(223,47%,8%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"
      }`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <div className="px-3 py-2.5 sm:px-4 sm:py-3">
        <div className="flex flex-col gap-2 sm:gap-3">
          {/* Title + body */}
          <div className="flex items-start gap-2 sm:gap-3">
            <Cookie className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-[13px] sm:text-sm font-bold mb-0.5 sm:mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.title}</p>
              <p className={`text-[11px] sm:text-xs leading-snug sm:leading-relaxed line-clamp-2 sm:line-clamp-none ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {t.body}
              </p>
              <Link to="/legal" className={`text-[11px] sm:text-xs underline hover:text-[#0EA5E9] ${isDark ? "text-slate-400" : "text-slate-600"}`} data-testid="cookie-banner-privacy-link">
                {t.privacy_link}
              </Link>
            </div>
          </div>

          {/* Granular toggles — collapsible */}
          {expanded && (
            <div className={`grid grid-cols-1 gap-2 p-3 rounded-xl ${isDark ? "bg-[hsl(223,47%,11%)]" : "bg-slate-50"}`} data-testid="cookie-toggles">
              {/* Necessary */}
              <div className="flex items-start gap-2">
                <input type="checkbox" checked readOnly className="mt-0.5 cursor-not-allowed opacity-60" data-testid="cookie-toggle-necessary" />
                <div>
                  <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.necessary_t}</p>
                  <p className={`text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.necessary_d}</p>
                </div>
              </div>
              {/* Analytics */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-0.5 cursor-pointer accent-[#10B981]"
                  data-testid="cookie-toggle-analytics"
                />
                <div>
                  <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.analytics_t}</p>
                  <p className={`text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.analytics_d}</p>
                </div>
              </label>
              {/* Marketing */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-0.5 cursor-pointer accent-[#10B981]"
                  data-testid="cookie-toggle-marketing"
                />
                <div>
                  <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.marketing_t}</p>
                  <p className={`text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.marketing_d}</p>
                </div>
              </label>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-row items-center gap-2 sm:gap-3 sm:justify-end">
            <button
              onClick={() => setExpanded((e) => !e)}
              className={`text-[11px] sm:text-xs font-medium inline-flex items-center justify-center gap-1 px-2 sm:px-3 h-8 sm:h-9 rounded-full shrink-0 ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"
              }`}
              data-testid="cookie-banner-customize"
            >
              {expanded ? (
                <>
                  {t.hide} <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  {t.customize} <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
            <Button
              variant="outline"
              onClick={() => persist({ analytics: false, marketing: false })}
              className={`flex-1 sm:flex-none h-8 sm:h-9 px-2 sm:px-4 rounded-full text-[11px] sm:text-xs font-semibold ${
                isDark ? "border-slate-700 text-slate-300" : "border-slate-300 text-slate-700"
              }`}
              data-testid="cookie-banner-reject"
            >
              {t.reject_all}
            </Button>
            {expanded ? (
              <Button
                onClick={() => persist({ analytics, marketing })}
                className="flex-1 sm:flex-none h-8 sm:h-9 px-2 sm:px-4 rounded-full text-[11px] sm:text-xs font-semibold bg-gradient-to-r from-[#10B981] to-[#0EA5E9] text-white"
                data-testid="cookie-banner-save"
              >
                {t.save}
              </Button>
            ) : (
              <Button
                onClick={() => persist({ analytics: true, marketing: true })}
                className="flex-1 sm:flex-none h-8 sm:h-9 px-2 sm:px-4 rounded-full text-[11px] sm:text-xs font-semibold bg-gradient-to-r from-[#10B981] to-[#0EA5E9] text-white"
                data-testid="cookie-banner-accept-all"
              >
                {t.accept_all}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
